import { useState } from "react";
import { createFileRoute, getRouteApi, useNavigate } from "@tanstack/react-router";
import type { CurrentUser } from "../../lib/auth";
import { TaskBoard } from "../../components/tasks/TaskBoard";
import { TaskModal } from "../../components/tasks/TaskModal";
import { TaskCreateModal } from "../../components/tasks/TaskCreateModal";
import { TaskMetrics } from "../../components/tasks/TaskMetrics";
import { ApiHelpModal } from "../../components/tasks/ApiHelpModal";
import { KanbanBoard } from "../../components/tasks/v2/KanbanBoard";
import { KanbanCardModal } from "../../components/tasks/v2/KanbanCardModal";
import { KanbanCreateModal } from "../../components/tasks/v2/KanbanCreateModal";
import type { Pipe } from "../../components/tasks/v2/common";

// Tarefas internas: kanban Hoje/Amanhã/Próximas/Finalizados + Métricas + Quadro v2 (por etapa).
// Regras de acesso no router (apps/api/.../routers/tasks.py).
export const Route = createFileRoute("/_authed/tarefas")({
  validateSearch: (s: Record<string, unknown>): { tab?: "metricas" | "v2" } => ({
    tab: s.tab === "metricas" ? "metricas" : s.tab === "v2" ? "v2" : undefined,
  }),
  component: TarefasPage,
});

const _authedRoute = getRouteApi("/_authed");

function TarefasPage() {
  const { user } = _authedRoute.useRouteContext() as { user?: CurrentUser };
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const isAdmin = user?.role_name === "Admin";
  const isViewer = !!user?.permissions?.viewer;
  const canMetrics = isAdmin || isViewer || !!user?.permissions?.relatorios;
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [apiHelp, setApiHelp] = useState(false);
  const showMetrics = tab === "metricas" && canMetrics;
  const showV2 = tab === "v2";
  const [pipe, setPipe] = useState<Pipe>("tasks");
  const [openCard, setOpenCard] = useState<string | null>(null);
  const [creatingCard, setCreatingCard] = useState(false);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Topbar */}
      <div style={{ height: 52, borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", padding: "0 24px", flexShrink: 0, gap: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Tarefas</span>
        <div style={{ flex: 1 }} />
        {isAdmin && (
          <button
            onClick={() => setApiHelp(true)}
            title="Como criar tarefas pela API"
            style={{ background: "transparent", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "6px 14px", fontSize: 13, color: "var(--ink-secondary)", cursor: "pointer" }}
          >
            API
          </button>
        )}
        {!isViewer && showV2 && (
          <button
            onClick={() => setCreatingCard(true)}
            style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "7px 16px", fontSize: 13, cursor: "pointer" }}
          >
            + Novo card
          </button>
        )}
        {!isViewer && !showV2 && (
          <button
            onClick={() => setCreating(true)}
            style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "7px 16px", fontSize: 13, cursor: "pointer" }}
          >
            + Nova tarefa
          </button>
        )}
      </div>

      {/* Abas */}
      <div style={{ display: "flex", borderBottom: "1px solid var(--line)", padding: "0 24px", flexShrink: 0 }}>
          {([["quadro", "Quadro"], ["v2", "Quadro v2"], ...(canMetrics ? [["metricas", "Métricas"]] as const : [])] as const).map(([id, label]) => {
            const active = (showMetrics ? "metricas" : showV2 ? "v2" : "quadro") === id;
            return (
              <button
                key={id}
                onClick={() => navigate({ to: "/tarefas", search: id === "quadro" ? {} : { tab: id }, replace: true })}
                style={{ padding: "10px 16px", fontSize: 13, background: "none", border: "none", borderBottom: `2px solid ${active ? "var(--brand)" : "transparent"}`, color: active ? "var(--ink)" : "var(--ink-muted)", cursor: "pointer", fontWeight: active ? 600 : 400 }}
              >
                {label}
              </button>
            );
          })}
      </div>

      {showMetrics ? <TaskMetrics /> : showV2 ? <KanbanBoard pipe={pipe} onPipe={setPipe} onOpen={setOpenCard} /> : <TaskBoard onOpen={setOpenId} />}

      {openCard && <KanbanCardModal cardId={openCard} onClose={() => setOpenCard(null)} />}
      {creatingCard && (
        <KanbanCreateModal pipe={pipe} onClose={() => setCreatingCard(false)} onCreated={id => { setCreatingCard(false); setOpenCard(id); }} />
      )}

      {openId && <TaskModal taskId={openId} onClose={() => setOpenId(null)} />}
      {creating && (
        <TaskCreateModal onClose={() => setCreating(false)} onCreated={id => { setCreating(false); setOpenId(id); }} />
      )}
      {apiHelp && <ApiHelpModal onClose={() => setApiHelp(false)} />}
    </div>
  );
}
